import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { ElMessage } from 'element-plus'
import {
  getMergedPermissionsFromLocalStorage,
  hasModulePermission,
  hasAppPageAccess,
  hasSurveyPageAccess
} from '@/utils/permissions'
import { isTabbable } from '@/utils/tabs'
import store from '@/store'
import UserManage from '@/views/admin/components/UserManage.vue'
import PermissionGroups from '@/views/admin/components/PermissionGroups.vue'
import OrderList from '@/views/orders/OrderList.vue'

const routes = [
  {
    path: '/',
    redirect: '/auth/login'
  },
  {
    path: '/learning-report',
    name: 'LearningReportPrototype',
    component: () => import('@/views/class/LearningReportPrototypeView.vue'),
    meta: {
      title: '课堂记录',
      noTab: true,
      permissions: [{ module: 'learning_report', action: 'view' }]
    }
  },
  {
    path: '/h5/report/:token',
    name: 'H5LearningReport',
    component: () => import('@/views/h5/H5LearningReportView.vue'),
    meta: {
      title: '学习报告',
      noTab: true
    }
  },
  {
    path: '/s/:shareToken',
    name: 'SurveyFill',
    component: () => import('@/views/survey/SurveyFillView.vue'),
    meta: { title: '问卷调查', noTab: true, bareTitle: true }
  },
  {
    path: '/h5/report-preview/:sessionId/:lessonIndex/:studentId',
    name: 'H5TeacherReportPreview',
    component: () => import('@/views/h5/H5TeacherReportPreview.vue'),
    meta: {
      title: '家长报告预览',
      noTab: true,
      requiresAuth: true
    }
  },
  {
    path: '/parent/login',
    name: 'ParentLogin',
    component: () => import('@/views/h5/parent/H5ParentLogin.vue'),
    meta: { title: '家长登录', noTab: true, public: true }
  },
  {
    path: '/v/:token',
    name: 'ParentVideoShare',
    component: () => import('@/views/h5/parent/H5ParentVideoShare.vue'),
    meta: { title: '课程回放', noTab: true, public: true }
  },
  {
    path: '/fv/:token',
    name: 'ParentVideoFolderShare',
    component: () => import('@/views/h5/parent/H5ParentVideoFolderShare.vue'),
    meta: { title: '课程视频文件夹', noTab: true, public: true }
  },
  {
    path: '/video-play/:videoId',
    name: 'StaffVideoPlayer',
    component: () => import('@/views/h5/parent/H5ParentVideoPlayer.vue'),
    meta: { title: '播放课程', noTab: true, requiresAuth: true, staffVideo: true }
  },
  {
    path: '/parent',
    name: 'ParentHome',
    component: () => import('@/views/h5/parent/H5ParentHome.vue'),
    meta: { title: '家长端', noTab: true }
  },
  {
    path: '/parent/report/:studentId/:sessionId/:lessonIndex',
    name: 'ParentReport',
    component: () => import('@/views/h5/parent/H5ParentReport.vue'),
    meta: { title: '学习报告', noTab: true }
  },
  {
    path: '/parent/settings',
    name: 'ParentSettings',
    component: () => import('@/views/h5/parent/H5ParentSettings.vue'),
    meta: { title: '设置', noTab: true }
  },
  {
    path: '/parent/homework/:studentId/:sessionId/:lessonIndex',
    name: 'ParentHomework',
    component: () => import('@/views/h5/parent/H5ParentHomework.vue'),
    meta: { title: '作业', noTab: true }
  },
  {
    path: '/parent/checkin/:studentId',
    name: 'ParentCheckIn',
    component: () => import('@/views/h5/parent/H5ParentCheckIn.vue'),
    meta: { title: '打卡', noTab: true }
  },
  {
    path: '/parent/activity/:activityId',
    name: 'ParentActivityDetail',
    component: () => import('@/views/h5/parent/H5ActivityDetail.vue'),
    meta: { title: '活动详情', noTab: true }
  },
  {
    path: '/signup/:activityId',
    name: 'ActivitySignup',
    component: () => import('@/views/h5/parent/H5Signup.vue'),
    meta: { title: '活动报名', noTab: true }
  },
  {
    path: '/parent/scores/:studentId',
    name: 'ParentScores',
    component: () => import('@/views/h5/parent/H5ParentScores.vue'),
    meta: { title: '成绩', noTab: true }
  },
  {
    path: '/parent/scores/:studentId/:recordId',
    name: 'ParentScoreDetail',
    component: () => import('@/views/h5/parent/H5ParentScoreDetail.vue'),
    meta: { title: '成绩详情', noTab: true }
  },
  {
    path: '/parent/videos/:studentId',
    name: 'ParentVideos',
    component: () => import('@/views/h5/parent/H5ParentVideos.vue'),
    meta: { title: '课程回看', noTab: true }
  },
  {
    path: '/parent/videos/:studentId/:videoId',
    name: 'ParentVideoPlayer',
    component: () => import('@/views/h5/parent/H5ParentVideoPlayer.vue'),
    meta: { title: '播放课程', noTab: true }
  },
  {
    path: '/homework-center',
    name: 'HomeworkCenter',
    component: () => import('@/views/homework/HomeworkCenter.vue'),
    meta: {
      title: '作业中心',
      requiresAuth: true,
      noTab: true,
      permissions: [{ module: 'homework', action: 'view' }]
    }
  },
  {
    path: '/homework-center/:id/review',
    name: 'HomeworkReview',
    component: () => import('@/views/homework/HomeworkReview.vue'),
    meta: {
      title: '批阅作业',
      requiresAuth: true,
      noTab: true,
      permissions: [{ module: 'homework', action: 'review' }]
    }
  },
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { title: '登录' }
      }
    ]
  },
  {
    path: '/main',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: '首页', affix: true }
      },
      {
        path: 'workbench',
        name: 'Workbench',
        component: () => import('@/views/workbench/WorkbenchView.vue'),
        meta: {
          title: '协作工作台',
          requiresAuth: true,
          permissions: [{ module: 'workbench', action: 'view' }]
        }
      },
      {
        path: 'student',
        name: 'Student',
        component: () => import('@/views/student/StudentView.vue'),
        meta: {
          title: '学员管理',
          requiresAuth: true,
          permissions: [
            { module: 'students', action: 'view_own' },
            { module: 'students', action: 'view_all' }
          ]
        }
      },
      {
        path: 'student/:id',
        name: 'StudentDetail',
        component: () => import('@/views/student/StudentDetailView.vue'),
        meta: {
          title: '学员详情',
          backTo: '/main/student',
          requiresAuth: true,
          permissions: [
            { module: 'students', action: 'view_own' },
            { module: 'students', action: 'view_all' }
          ]
        }
      },
      {
        path: 'student/:id/wrong-questions',
        name: 'StudentWrongQuestions',
        component: () => import('@/views/student/StudentWrongQuestionsView.vue'),
        meta: {
          title: '学员错题库',
          backTo: '/main/student/:id',
          requiresAuth: true,
          permissions: [
            { module: 'students', action: 'view_own' },
            { module: 'students', action: 'view_all' }
          ]
        }
      },
      {
        path: 'course',
        name: 'Course',
        component: () => import('@/views/course/CourseManageView.vue'),
        meta: {
          title: '课程管理',
          requiresAuth: true,
          permissions: [{ module: 'courses', action: 'view' }]
        }
      },
      {
        path: 'course/:id',
        name: 'CourseDetail',
        component: () => import('@/views/course/CourseDetailView.vue'),
        meta: {
          title: '课程详情',
          requiresAuth: true,
          permissions: [{ module: 'courses', action: 'view' }],
          backTo: '/main/course'
        }
      },
      {
        path: 'course-renewal-time',
        name: 'GradeScheduleMapping',
        component: () => import('@/views/course/GradeScheduleMappingView.vue'),
        meta: {
          title: '续报时间映射',
          requiresAuth: true,
          permissions: [{ module: 'courses', action: 'view' }],
          backTo: '/main/course'
        }
      },
      {
        path: 'class',
        name: 'Class',
        component: () => import('@/views/class/ClassView.vue'),
        meta: {
          title: '班级管理',
          requiresAuth: true,
          permissions: [
            { module: 'classes', action: 'view_own' },
            { module: 'classes', action: 'view_all' }
          ]
        }
      },
      {
        path: 'class/:id/arrange',
        name: 'ArrangeClass',
        component: () => import('@/views/class/ArrangeClassView.vue'),
        meta: {
          title: '班级排课',
          backTo: '/main/class',
          permissions: [{ module: 'classes', action: 'access_class_arrange' }]
        }
      },
      {
        path: 'class/:id/students',
        name: 'ClassStudents',
        component: () => import('@/views/class/ClassStudentView.vue'),
        meta: {
          title: '班级学员',
          backTo: '/main/class',
          requiresAuth: true,
          permissions: [
            { module: 'classes', action: 'view_own' },
            { module: 'classes', action: 'view_all' }
          ]
        }
      },
      {
        path: 'class/:id/schedule',
        name: 'ClassSchedule',
        component: () => import('@/views/class/ClassScheduleView.vue'),
        meta: {
          title: '班级课表',
          backTo: '/main/class',
          requiresAuth: true,
          permissions: [
            { module: 'classes', action: 'view_own' },
            { module: 'classes', action: 'view_all' }
          ]
        }
      },
      {
        path: 'class/:id/stage-review',
        name: 'ClassStageReview',
        component: () => import('@/views/class/StageReviewView.vue'),
        meta: {
          title: '阶段学情回顾',
          requiresAuth: true,
          roles: ['teacher', 'admin', 'superadmin'],
          permissions: [
            { module: 'classes', action: 'view_own' },
            { module: 'classes', action: 'view_all' }
          ]
        }
      },
      {
        path: 'schedule-templates',
        name: 'ScheduleTemplates',
        component: () => import('@/views/class/ScheduleTemplateView.vue'),
        meta: {
          title: '排课模板',
          requiresAuth: true,
          permissions: [
            { module: 'classes', action: 'view_own' },
            { module: 'classes', action: 'view_all' }
          ]
        }
      },
      {
        path: 'schedule-manage',
        name: 'ScheduleManage',
        component: () => import('@/views/schedule/ScheduleManageView.vue'),
        meta: {
          title: '排课管理',
          requiresAuth: true,
          permissions: [{ module: 'app_pages', action: 'schedule_manage' }]
        }
      },
      {
        path: 'activity',
        name: 'ActivityList',
        component: () => import('@/views/activity/ActivityList.vue'),
        meta: {
          title: '活动',
          requiresAuth: true,
          permissions: [{ module: 'activity', action: 'view' }]
        }
      },
      {
        path: 'activity/:id/members',
        name: 'ActivityMembers',
        component: () => import('@/views/activity/ActivityMembers.vue'),
        meta: {
          title: '活动成员',
          backTo: '/main/activity',
          requiresAuth: true,
          permissions: [{ module: 'activity', action: 'view' }]
        }
      },
      {
        path: 'checkin',
        name: 'CheckInPlanList',
        component: () => import('@/views/checkin/CheckInPlanList.vue'),
        meta: {
          title: '打卡',
          requiresAuth: true,
          permissions: [{ module: 'checkin', action: 'view' }]
        }
      },
      {
        path: 'notifications',
        name: 'NotificationsCenter',
        component: () => import('@/views/notifications/NotificationsCenter.vue'),
        meta: {
          title: '通知中心',
          requiresAuth: true,
          backTo: '/main/home'
        }
      },
      {
        path: 'checkin/:id/progress',
        name: 'CheckInProgress',
        component: () => import('@/views/checkin/CheckInProgress.vue'),
        meta: {
          title: '打卡进度',
          backTo: '/main/checkin',
          requiresAuth: true,
          permissions: [{ module: 'checkin', action: 'view' }]
        }
      },
      {
        path: 'checkin/review',
        name: 'CheckInReviewList',
        component: () => import('@/views/checkin/CheckInReviewList.vue'),
        meta: {
          title: '打卡批改',
          backTo: '/main/checkin',
          requiresAuth: true,
          permissions: [{ module: 'checkin', action: 'view' }]
        }
      },
      {
        path: 'checkin/:id/records',
        name: 'CheckInRecords',
        component: () => import('@/views/checkin/CheckInRecords.vue'),
        meta: {
          title: '打卡批改',
          backTo: '/main/checkin',
          requiresAuth: true,
          permissions: [{ module: 'checkin', action: 'view' }]
        }
      },
      {
        path: 'checkin/record/:id',
        name: 'CheckInReview',
        component: () => import('@/views/checkin/CheckInReview.vue'),
        meta: {
          title: '批改打卡',
          requiresAuth: true,
          hideLayout: true,  // 隐藏侧边栏和顶部栏
          noTab: true,
          permissions: [{ module: 'checkin', action: 'view' }]
        }
      },
      {
        path: 'files',
        name: 'Files',
        component: () => import('@/views/files/FilesView.vue'),
        meta: { title: '我的文件' }
      },
      {
        path: 'videos',
        name: 'VideoManage',
        component: () => import('@/views/admin/VideoManageView.vue'),
        meta: { title: '课程视频', requiresAuth: true }
      },
      {
        path: 'downloads',
        name: 'ClientDownloads',
        component: () => import('@/views/downloads/ClientDownloadsView.vue'),
        meta: {
          title: '客户端下载',
          requiresAuth: true
        }
      },
      {
        path: 'assets',
        name: 'AssetList',
        component: () => import('@/views/assets/AssetListView.vue'),
        meta: {
          title: '资产管理',
          requiresAuth: true,
          permissions: [{ module: 'assets', action: 'view_asset' }]
        }
      },
      {
        path: 'assets/categories',
        name: 'AssetCategories',
        component: () => import('@/views/assets/AssetCategoryView.vue'),
        meta: {
          title: '资产分类',
          requiresAuth: true,
          permissions: [{ module: 'assets', action: 'manage_asset_category' }]
        }
      },
      {
        path: 'assets/inventory',
        name: 'InventoryList',
        component: () => import('@/views/assets/InventoryListView.vue'),
        meta: {
          title: '资产盘点',
          requiresAuth: true,
          permissions: [{ module: 'assets', action: 'view_asset' }]
        }
      },
      {
        path: 'assets/inventory/:id',
        name: 'InventoryDetail',
        component: () => import('@/views/assets/InventoryDetailView.vue'),
        meta: {
          title: '盘点详情',
          requiresAuth: true,
          permissions: [{ module: 'assets', action: 'view_asset' }]
        }
      },
      {
        path: 'assets/scan/:code',
        name: 'AssetScan',
        component: () => import('@/views/assets/AssetListView.vue'),
        meta: {
          title: '资产扫码',
          requiresAuth: true,
          permissions: [{ module: 'assets', action: 'view_asset' }]
        }
      },
      {
        path: 'document/:id',
        name: 'Document',
        component: () => import('@/views/document/DocumentView.vue'),
        meta: {
          title: '文档编辑',
          requiresAuth: true,
          hideLayout: true,  // 隐藏侧边栏和顶部栏
          noTab: true
        }
      },
      {
        path: 'document/:id/slides',
        name: 'SlideEditor',
        component: () => import('@/views/document/SlideEditorView.vue'),
        meta: {
          title: '课件编辑',
          requiresAuth: true,
          hideLayout: true,
          noTab: true
        }
      },
      {
        path: 'real-paper/:id',
        name: 'RealPaperDetail',
        component: () => import('@/views/realPaper/RealPaperDetailView.vue'),
        meta: {
          title: '真题详情',
          requiresAuth: true,
          hideLayout: true,
          noTab: true
        }
      },
      {
        path: 'scan-import-review/:batchId/:batchPaperId',
        name: 'ScanImportReview',
        component: () => import('@/views/realPaper/ScanImportReviewView.vue'),
        meta: {
          title: '扫描导入 · 题目确认',
          requiresAuth: true,
          hideLayout: true,
          noTab: true
        }
      },
      {
        path: 'admin',
        name: 'Admin',
        component: () => import('@/views/admin/AdminView.vue'),
        meta: { 
          title: '管理后台',
          requiresAuth: true,
          requiresAdmin: true,
          roles: ['admin', 'superadmin', 'academic']
        },
        children: [
          {
            path: '',  // 默认子路由
            name: 'AdminDashboard',
            component: () => import('@/views/admin/AdminDashboardView.vue'),
            meta: { title: '管理后台' }
          },
          {
            path: 'users',
            name: 'UserManage',
            component: UserManage,
            meta: {
              title: '用户管理',
              requiresAuth: true,
              roles: ['admin', 'superadmin']
            }
          },
          {
            path: 'permission-groups',
            name: 'PermissionGroups',
            component: PermissionGroups,
            meta: {
              title: '权限组管理',
              requiresAuth: true,
              roles: ['admin', 'superadmin']
            }
          },
          {
            path: 'operation-logs',
            name: 'OperationLog',
            component: () => import('@/views/admin/OperationLogView.vue'),
            meta: {
              title: '操作日志',
              requiresAuth: true,
              roles: ['admin', 'superadmin']
            }
          },
          {
            path: 'categories',
            name: 'CategoryManage',
            component: () => import('@/views/admin/CategoryManageView.vue'),
            meta: { title: '分类管理' }
          },
          {
            path: 'questions',
            name: 'QuestionManage',
            component: () => import('@/views/admin/QuestionManageView.vue'),
            meta: { title: '题库管理' }
          },
          {
            path: 'campus',
            name: 'CampusManage',
            component: () => import('@/views/admin/CampusManageView.vue'),
            meta: { title: '校区和教室管理' }
          },
          {
            path: 'staff-punches',
            name: 'StaffPunchAdmin',
            component: () => import('@/views/admin/StaffPunchAdminView.vue'),
            meta: {
              title: '打卡记录',
              requiresAuth: true,
              roles: ['admin', 'superadmin']
            }
          },
          {
            path: 'videos',
            redirect: '/main/videos'
          },
          {
            path: 'font-manage',
            name: 'FontManage',
            component: () => import('@/views/admin/FontManageView.vue'),
            meta: {
              title: '手写字体管理',
              requiresAuth: true,
              roles: ['admin', 'superadmin']
            }
          },
          {
            path: 'teachers',
            redirect: () => ({ name: 'UserManage', query: { role: 'teacher' } })
          }
        ]
      },
      {
        path: 'enrollment',
        name: 'Enrollment',
        component: () => import('@/views/enrollment/EnrollmentView.vue'),
        meta: {
          title: '学员报名',
          icon: 'Edit',
          permissions: [{ module: 'enrollments', action: 'view' }]
        }
      },
      {
        path: 'assessment',
        name: 'Assessment',
        component: () => import('@/views/assessment/NewStudentTestView.vue'),
        meta: {
          title: '新生测试',
          requiresAuth: true,
          permissions: [{ module: 'app_pages', action: 'new_student_test' }]
        }
      },
      {
        path: 'scores',
        name: 'ScoreTests',
        component: () => import('@/views/score/ScoreTestView.vue'),
        meta: {
          title: '成绩管理',
          requiresAuth: true,
          roles: ['admin', 'superadmin', 'teacher']
        }
      },
      {
        path: 'orders',
        name: 'orders',
        component: OrderList,
        meta: {
          requiresAuth: true,
          title: '订单管理',
          permissions: [{ module: 'orders', action: 'view' }]
        }
      },
      {
        path: 'payment-reconciliation',
        name: 'PaymentReconciliation',
        component: () => import('@/views/orders/PaymentReconciliationView.vue'),
        meta: {
          requiresAuth: true,
          title: '收款对账',
          permissions: [
            { module: 'reconciliations', action: 'view' },
            { module: 'reconciliations', action: 'submit' },
            { module: 'reconciliations', action: 'review' }
          ]
        }
      },
      {
        path: 'lessons',
        name: 'TeacherLessons',
        component: () => import('@/views/lessons/TeacherLessonsView.vue'),
        meta: {
          title: '记课时',
          requiresAuth: true,
          permissions: [{ module: 'lessons', action: 'view' }]
        }
      },
      {
        path: 'refunds',
        name: 'RefundApplications',
        component: () => import('@/views/refunds/RefundApplicationsView.vue'),
        meta: {
          title: '退费管理',
          requiresAuth: true,
          permissions: [
            { module: 'refunds', action: 'apply_own' },
            { module: 'refunds', action: 'apply_all' },
            { module: 'refunds', action: 'view_own' },
            { module: 'refunds', action: 'upload_voucher' },
            { module: 'refunds', action: 'review' },
            { module: 'refunds', action: 'settle' },
            { module: 'refunds', action: 'view_all' }
          ]
        }
      },
      {
        path: 'staff-punch',
        name: 'StaffPunch',
        component: () => import('@/views/staff/StaffPunchView.vue'),
        meta: {
          title: '打卡',
          requiresAuth: true,
          roles: ['admin', 'superadmin', 'teacher', 'academic']
        }
      },
      {
        path: 'questions',
        name: 'QuestionBank',
        component: () => import('@/views/admin/QuestionManageView.vue'),
        meta: {
          title: '题库',
          requiresAuth: true,
          permissions: [{ module: 'app_pages', action: 'question_bank' }]
        }
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/analytics/AnalyticsView.vue'),
        meta: {
          title: '数据统计',
          requiresAuth: true,
          permissions: [{ module: 'app_pages', action: 'analytics' }]
        }
      },
      {
        path: 'renewal-students',
        name: 'RenewalStudents',
        component: () => import('@/views/analytics/RenewalStudentsView.vue'),
        meta: {
          title: '续报人员',
          requiresAuth: true,
          permissions: [{ module: 'students', action: 'renewal_student' }]
        }
      },
      {
        path: 'settings',
        name: 'SystemSettings',
        component: () => import('@/views/settings/SystemSettingsView.vue'),
        meta: {
          title: '系统设置',
          requiresAuth: true
        }
      },
      {
        path: 'survey',
        name: 'SurveyList',
        component: () => import('@/views/survey/SurveyListView.vue'),
        meta: { title: '问卷调查', requiresAuth: true, permissions: [{ module: 'survey_page', action: 'view' }] }
      },
      {
        path: 'survey/new',
        name: 'SurveyCreate',
        component: () => import('@/views/survey/SurveyEditorView.vue'),
        meta: { title: '新建问卷', requiresAuth: true, noTab: true, backTo: '/main/survey', permissions: [{ module: 'survey_page', action: 'view' }] }
      },
      {
        path: 'survey/:id/edit',
        name: 'SurveyEdit',
        component: () => import('@/views/survey/SurveyEditorView.vue'),
        meta: { title: '编辑问卷', requiresAuth: true, noTab: true, backTo: '/main/survey', permissions: [{ module: 'survey_page', action: 'view' }] }
      },
      {
        path: 'survey/:id/results',
        name: 'SurveyResults',
        component: () => import('@/views/survey/SurveyResultsView.vue'),
        meta: { title: '问卷结果', requiresAuth: true, noTab: true, backTo: '/main/survey', permissions: [{ module: 'survey_page', action: 'view' }] }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 路由守卫
// 懒刷新用户权限快照：管理员改权限后，用户下次进入页面即生效，无需退出重登。
// 节流 60 秒，避免每次路由切换都打 /users/me。
let lastUserInfoRefreshAt = 0
const USER_INFO_REFRESH_MS = 60 * 1000
async function refreshUserInfoIfNeeded() {
  if (Date.now() - lastUserInfoRefreshAt < USER_INFO_REFRESH_MS) return
  lastUserInfoRefreshAt = Date.now()
  try {
    await store.dispatch('auth/fetchUserInfo')
  } catch (e) {
    // 刷新失败（未登录/网络异常）不阻断导航；401 已由拦截器跳转登录页
  }
}

router.beforeEach(async (to, from, next) => {
  NProgress.start()

  // 家长端路由：登录态为 httpOnly cookie，JS 读不到，故由服务端为准。
  // 这里一律放行，未登录会在接口返回 401 时由响应拦截器跳转登录页。
  // /signup 为公开报名页，同样放行。
  if (to.path.startsWith('/parent') || to.path.startsWith('/signup') || ['ParentVideoShare', 'ParentVideoFolderShare'].includes(to.name)) {
    if (to.meta.title) document.title = `${to.meta.title}${to.path.startsWith('/parent') ? ' - 家长端' : ''}`
    next()
    return
  }

  const token = localStorage.getItem('token')
  let userRole = localStorage.getItem('role')
  let userPermissions = getMergedPermissionsFromLocalStorage()
  const checkMetaPermission = (moduleName, action) => {
    if (moduleName === 'survey_page') {
      return action === 'view' && hasSurveyPageAccess(userRole, userPermissions)
    }
    if (moduleName === 'app_pages') {
      return hasAppPageAccess(userRole, userPermissions, action)
    }
    return hasModulePermission(userRole, userPermissions, moduleName, action)
  }
  
  console.log('Navigation:', {
    to: to.path,
    userRole,
    requiresAuth: to.meta.requiresAuth,
    roles: to.meta.roles,
    matched: to.matched.map(record => ({
      path: record.path,
      roles: record.meta?.roles
    }))
  })
  
  // 如果是访问登录页面，直接通过
  if (to.path === '/auth/login') {
    next()
    return
  }
  
  // 需要登录的页面
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token) {
      // 静默重定向到登录页面，不显示错误消息
      next({ path: '/auth/login', replace: true })
      return
    }
    // 刷新后重算权限与角色，保证本次进入页面即用最新权限
    await refreshUserInfoIfNeeded()
    userRole = localStorage.getItem('role')
    userPermissions = getMergedPermissionsFromLocalStorage()
  }

  // 需要特定角色权限的页面（嵌套路由以最深一条带 roles 的为准，避免子页被父级宽松角色放行）
  if (to.matched.some(record => record.meta.roles)) {
    const recordsWithRoles = to.matched.filter((record) => record.meta.roles)
    const allowedRoles = recordsWithRoles[recordsWithRoles.length - 1].meta.roles
    if (!allowedRoles.includes(userRole)) {
      ElMessage.error('没有访问权限')
      next({ name: 'Files' })
      return
    }
  }

  // 禁止特定角色（例如课程页不对教师开放）
  const forbiddenRoles = to.matched.flatMap((record) => record.meta.forbidRoles || [])
  if (userRole && forbiddenRoles.includes(userRole)) {
    ElMessage.error('没有访问权限')
    next({ name: 'Files' })
    return
  }

  // 需要特定权限的页面
  if (to.matched.some(record => record.meta.permissions)) {
    const requiredPermissions = to.matched
      .flatMap(record => record.meta.permissions || [])
      .filter(item => item && item.module && item.action)

    const permissionPass = requiredPermissions.length === 0
      ? true
      : requiredPermissions.some(item => checkMetaPermission(item.module, item.action))

    if (!permissionPass) {
      ElMessage.error('没有访问权限')
      next({ name: 'Files' })
      return
    }
  }

  // 设置页面标题
  if (to.meta.title) {
    document.title = to.meta.bareTitle ? to.meta.title : `${to.meta.title} - 优启学素养中心`
  }
  
  next()
})

router.afterEach((to) => {
  NProgress.done()
  if (isTabbable(to)) {
    store.dispatch('tabs/addTabFromRoute', to)
  }
})

export default router 
